import React, { useState } from "react";
import {
  Card,
  Center,
  Heading,
  Flex,
  Box,
  Select,
  Grid,
} from "@chakra-ui/react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { BiArrowBack } from "react-icons/bi";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

// Custom Components
// import CustomInput from "../../components/CustomInput";
import CustomButton from "../../components/ButtonComp";
import SelectImage from "../../components/SelectImage";
import CustomSingleInput from "../../components/CustomSingleInput";
import { bankDetails } from '../../utils/data'; // Add this import
import { baseUrl } from "../../utils/utils";

const BankSchema = Yup.object().shape({
  name: Yup.string().required("Bank name is required"),
  branchName: Yup.string().required("Branch name is required"),
  accType: Yup.string().required("Account type is required"),
  accNo: Yup.string().required("Account number is required"),
  ifscno: Yup.string().required("IFSC code is required"),
  openingbal: Yup.string().required("Opening balance is required"),
  id: Yup.string().required("UPI ID is required"),
  qrCodeFile: Yup.mixed().required("QR code image is required"),
});

const AddBank = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Remove API-related states since we're using static data
  // const [bankNames, setBankNames] = useState([]);
  // const [loadingBanks, setLoadingBanks] = useState(false);

  const initialValues = {
    name: "",
    branchName: "",
    accType: "",
    accNo: "",
    ifscno: "",
    openingbal: "",
    id: "Test@123",
    qrCodeFile: null,
  };

  // Remove the fetchBankNames function and useEffect since we're using static data

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      setIsSubmitting(true);

      const formData = new FormData();
      formData.append("action", "create");
      formData.append("bank_name", values.name);
      formData.append("bank_branch", values.branchName);
      formData.append("ac_type", values.accType);
      formData.append("accountno", values.accNo);
      formData.append("ifsc", values.ifscno);
      formData.append("acc_ob", values.openingbal);
      formData.append("upi_id", values.id);
      formData.append("qr_code_image", values.qrCodeFile);

      const response = await fetch(baseUrl + "bank.php", {
        method: "POST",
        body: formData,
      });

      const result = await response.json();
      
      if (result.success) {
        toast.success(result.message);
        resetForm();
        navigate("/banks");
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error(error.message || "Failed to Add Bank");
    } finally {
      setIsSubmitting(false);
      setSubmitting(false);
    }
  };

  return (
    <>
      <Card
        display={"flex"}
        flexDirection={"row"}
        alignItems={"center"}
        justify={"space-between"}
        paddingInline={5}
        paddingBlock={2}
        mb={4}
      >
        <Heading fontSize={"xl"}>Add Bank</Heading>
        <CustomButton
          icon={<BiArrowBack color="#fff" />}
          title={"Back"}
          height={"30px"}
          b_color={"#000"}
          onClick={() => navigate(-1)}
        />
      </Card>

      <Formik
        initialValues={initialValues}
        validationSchema={BankSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          handleChange,
          handleBlur,
          setFieldValue,
          touched,
          errors,
          submitForm,
        }) => (
          <Form>
            <Card paddingInline={5} paddingBlock={5}>
              <Grid templateColumns={["1fr", null, "repeat(2, 1fr)"]} gap={4} mb={4}>
                {/* Bank Name Dropdown */}
                <Box>
                  <label style={{ fontSize: "14px", fontWeight: "500", marginBottom: "8px", display: "block" }}>
                    Bank Name <span style={{ color: "red" }}>*</span>
                  </label>
                  <Select
                    placeholder="Select bank name"
                    value={values.name}
                    onChange={handleChange("name")}
                    onBlur={handleBlur("name")}
                    borderColor={touched.name && errors.name ? "red.500" : "gray.300"}
                  >
                    {bankDetails?.map((v, i) => (
                      <option value={v.bankname} key={i}>
                        {v.bankname}
                      </option>
                    ))}
                  </Select>
                  {touched.name && errors.name && (
                    <Box color="red.500" fontSize="sm" mt={1}>
                      {errors.name}
                    </Box>
                  )}
                </Box>

                {/* Bank Branch */}
                <CustomSingleInput
                  label="Bank Branch"
                  value={values.branchName}
                  onChange={handleChange("branchName")}
                  onBlur={handleBlur("branchName")}
                  placeholder="Enter branch name"
                  error={touched.branchName && errors.branchName}
                  required={true}
                />
              </Grid>
              <Grid templateColumns={["1fr", null, "repeat(2, 1fr)"]} gap={4} mb={4}>
                {/* Account Type Dropdown */}
               <Box>
  <label style={{ fontSize: "14px", fontWeight: "500", marginBottom: "8px", display: "block" }}>
    Account Type <span style={{ color: "red" }}>*</span>
  </label>
  <Select
    placeholder="Select account type"
    value={values.accType}
    onChange={handleChange("accType")}
    onBlur={handleBlur("accType")}
    borderColor={touched.accType && errors.accType ? "red.500" : "gray.300"}
    disabled={!!values.accType} // Disable once value is selected
  >
    <option value="savings">Savings</option>
    <option value="current">Current</option>
  </Select>
  {touched.accType && errors.accType && (
    <Box color="red.500" fontSize="sm" mt={1}>
      {errors.accType}
    </Box>
  )}
</Box>


                {/* Account Number */}
                <CustomSingleInput
                  label="Account Number"
                  value={values.accNo}
                  onChange={handleChange("accNo")}
                  onBlur={handleBlur("accNo")}
                  placeholder="Enter account number"
                  error={touched.accNo && errors.accNo}
                  required={true}
                />
              </Grid>

              <Grid templateColumns={["1fr", null, "repeat(2, 1fr)"]} gap={4}>
                {/* IFSC Code */}
                <CustomSingleInput
                  label={"IFSC Code"}
                  value={values.ifscno} 
                  onChange={handleChange("ifscno")}
                  onBlur={handleBlur("ifscno")}
                  placeholder={"Enter IFSC code"}
                  error={touched.ifscno && errors.ifscno}
                  required={true}
                />
                
                {/* QR Code Image */}
                <SelectImage
                  mt={0.01}
                  label={"QR Code Image"}
                  filename={values.qrCodeFile?.name}
                  error={touched.qrCodeFile && errors.qrCodeFile}
                  onChange={(file) => setFieldValue("qrCodeFile", file)}
                  required
                />

                <CustomSingleInput
                  label={"Opening Balance"}
                  value={values.openingbal}
                  onChange={handleChange("openingbal")}
                  onBlur={handleBlur("openingbal")}
                  placeholder={"Enter opening balance"}
                  error={touched.openingbal && errors.openingbal}
                  required={true}
                />

                <CustomSingleInput
                  label={"UPI ID"}
                  value={values.id}
                  onChange={handleChange("id")}
                  onBlur={handleBlur("id")}
                  placeholder={"Enter UPI ID"}
                  error={touched.id && errors.id}
                  required={true}
                />
              </Grid>

              <Center mt={6}>
                <CustomButton
                  title={isSubmitting ? "Submitting..." : "Add Bank"}
                  onClick={submitForm}
                  disabled={isSubmitting}
                />
              </Center>
            </Card>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default AddBank;